import { Component, NgZone } from '@angular/core';
import { catchError, switchMap } from 'rxjs/operators';

import { AuthService } from 'src/app/core/services/auth.service';
import { ConfirmationDialogComponent } from 'src/app/shared/confirmation-dialog/confirmation-dialog.component';
import { EMPTY } from 'rxjs';
import { Location } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';

interface Slot {
  slot_id: string;
  slot_time: any;
}

interface Lawyer {
  lawyer_id: string;
  amount?: number;
}

interface PriceLineItem {
  key: string;
  label: string;
  amount: number;
  type: string;
  refundable: boolean | null;
}

interface PriceSummary {
  context?: string;
  currency?: string;
  line_items: PriceLineItem[];
  totals: {
    paid_amount?: number;
    final_amount: number;
    final_amount_label?: string;
  };
}

@Component({
    selector: 'app-checkout',
    templateUrl: './checkout.component.html',
    styleUrls: ['./checkout.component.css'],
    standalone: false,
})
export class CheckoutComponent {
  Razorpay: any;
  RAZORPAY_KEY = 'rzp_test_SYb7zhnm847Zbj';
  slot!: Slot;
  lawyer!: Lawyer;
  couponCode: string = '';

  // Form fields — bound to the template via [(ngModel)]
  firstName: string = '';
  lastName: string = '';
  phone: string = '';
  email: string = '';

  // Validation errors
  firstNameError: string = '';
  lastNameError: string = '';
  phoneError: string = '';
  emailError: string = '';

  // Form submitted flag
  formSubmitted: boolean = false;
  appId: string = '';
  profileData: any;
  updatePayload: any;

  // Navigation source — used by goBack() to return to the correct screen
  source: string = '';
  firmId: string = '';

  // Stores the formatted dob from the profile API (null-safe)
  fdob: string = '';

  // Lock Email and Mobile once populated — these are the verified account
  // identifiers and must not be editable on the checkout screen.
  isEmailReadonly: boolean = false;
  isMobileReadonly: boolean = false;

  priceSummary: PriceSummary | null = null;
  isPriceSummaryExpanded: boolean = true;
  loadingPriceSummary: boolean = false;
  // True when the preview API failed (or returned no usable price summary).
  // Booking is blocked until a valid final_amount is loaded.
  previewError: boolean = false;

  // Final payable amount from the preview API (0 until a valid preview loads).
  get finalAmount(): number {
    return this.priceSummary?.totals?.final_amount ?? 0;
  }

  // Coupon state
  couponApplied: boolean = false;
  couponDiscount: number = 0;
  couponMessage: string = '';
  couponError: string = '';
  applyingCoupon: boolean = false;

  constructor(
    private auth: AuthService,
    private router: Router,
    private location: Location,
    private toastr: ToastrService,
    private ngZone: NgZone,
    public dialog: MatDialog,
  ) {}

  ngOnInit() {
    // Defensive: the bookslot / firm-details login modal locks body scroll
    // (overflow: hidden). If navigation landed here with that lock still in
    // place, the checkout page would be stuck fitted to the screen.
    document.body.style.overflow = '';

    let state = history.state;

    if (!state?.slot) {
      const savedState = sessionStorage.getItem('checkout');
      state = savedState ? JSON.parse(savedState) : null;
    }

    if (!state) {
      this.router.navigate(['/consumer']);
      return;
    }

    this.slot = state.slot;
    this.lawyer = state.lawyer;
    this.source = state.source || '';
    this.firmId = state.firmId || '';

    // Populate form immediately from localStorage so fields are not blank
    // while the profile API is in-flight.
    this.firstName =
      localStorage.getItem('first_name')?.replace(/^(null|undefined)$/, '') ??
      '';
    this.lastName =
      localStorage.getItem('last_name')?.replace(/^(null|undefined)$/, '') ??
      '';
    this.email =
      localStorage.getItem('email')?.replace(/^(null|undefined)$/, '') ?? '';
    this.phone =
      localStorage.getItem('mobile')?.replace(/^(null|undefined)$/, '') ?? '';

    this.isEmailReadonly = !!this.email;
    this.isMobileReadonly = !!this.phone;

    // Fetch profile and OVERRIDE form fields with the freshest API data.
    this.getProfile();
    this.getCheckoutPreview();
  }

  getProfile() {
    this.auth.get_digicoffer_profile().subscribe({
      next: (user: any) => {
        this.profileData = user?.data?.profile;

        if (!this.profileData) {
          // Profile data unavailable — keep the localStorage values already set.
          return;
        }

        // BUG FIX 1: dob can be null from the API (confirmed in screenshot).
        // Guard against calling .replace() on null.
        const rawDob: string | null = this.profileData.dob ?? null;
        this.fdob = rawDob ? rawDob.replace(/\//g, '-') : '';

        // BUG FIX 2: Form fields must be set INSIDE the subscribe callback so
        // the async API response actually reaches the template.
        // Profile API value takes priority; fall back to localStorage if blank.
        this.firstName =
          this.profileData.first_name?.trim() ||
          localStorage
            .getItem('first_name')
            ?.replace(/^(null|undefined)$/, '') ||
          '';

        this.lastName =
          this.profileData.last_name?.trim() ||
          localStorage
            .getItem('last_name')
            ?.replace(/^(null|undefined)$/, '') ||
          '';

        this.email =
          this.profileData.email?.trim() ||
          localStorage.getItem('email')?.replace(/^(null|undefined)$/, '') ||
          '';

        this.phone =
          this.profileData.mobile?.trim() ||
          localStorage.getItem('mobile')?.replace(/^(null|undefined)$/, '') ||
          '';

        // Lock Email and Mobile once populated from the API (or localStorage
        // fallback) — these are the verified account identifiers.
        this.isEmailReadonly = !!this.email;
        this.isMobileReadonly = !!this.phone;
      },
      error: () => {
        // Profile fetch failed — localStorage fallback values already set in ngOnInit.
        //this.toastr.warning('Could not load profile. Please verify your details before booking.');
      },
    });
  }

  validateForm(): boolean {
    this.formSubmitted = true;
    let isValid = true;

    // Reset errors
    this.firstNameError = '';
    this.lastNameError = '';
    this.phoneError = '';
    this.emailError = '';

    if (!this.firstName || this.firstName.trim() === '') {
      this.firstNameError = 'First name is required';
      isValid = false;
    }

    if (!this.lastName || this.lastName.trim() === '') {
      this.lastNameError = 'Last name is required';
      isValid = false;
    }

    if (!this.phone || this.phone.trim() === '') {
      this.phoneError = 'Phone number is required';
      isValid = false;
    } else if (!/^[0-9]{10}$/.test(this.phone.trim())) {
      this.phoneError = 'Please enter a valid 10-digit phone number';
      isValid = false;
    }

    if (!this.email || this.email.trim() === '') {
      this.emailError = 'Email address is required';
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email.trim())) {
      this.emailError = 'Please enter a valid email address';
      isValid = false;
    }

    return isValid;
  }

  goBack() {
    const appId = localStorage.getItem('appointment_id');

    // When coming from firm-details, use location.back() instead of router.navigate()
    // to pop the browser history stack directly. This avoids re-triggering the auth
    // guard on /firm-details which would otherwise redirect to /login.
    const navigateBack = () => {
      if (this.source === 'firm-details') {
        this.location.back();
      } else {
        this.router.navigate(['/consumer/bookslot']);
      }
    };

    if (!appId) {
      navigateBack();
      return;
    }

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      disableClose: true,
      data: {
        title: 'Confirmation',
        message: 'Are you sure you want to leave this page?',
        confirmText: 'Yes',
        cancelText: 'No',
      },
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.auth.cancelBookslot(appId).subscribe(
          () => {
            localStorage.removeItem('appointment_id');
            navigateBack();
          },
          (err) => {
            localStorage.removeItem('appointment_id');
            console.error('Cancel failed', err);
            navigateBack();
          },
        );
      }
    });
  }

  payAppointment() {
    if (!this.validateForm()) {
      return;
    }

    // Never book/charge without the GST/platform-fee-inclusive amount from
    // the preview API — booking with a zero/undefined amount would create
    // an appointment and order at the wrong price.
    if (
      this.previewError ||
      this.loadingPriceSummary ||
      !(this.finalAmount > 0)
    ) {
      this.toastr.error(
        'Price details could not be loaded. Please retry before booking.',
      );
      if (!this.loadingPriceSummary) {
        this.getCheckoutPreview();
      }
      return;
    }

    const appointmentPayload = {
      lawyer_id: this.lawyer.lawyer_id,
      slot_id: this.slot.slot_id,
    };

    this.updatePayload = {
      first_name: this.firstName.trim(),
      last_name: this.lastName.trim(),
      email: this.email.trim(),
      mobile: this.phone.trim(),
      dob: this.fdob || null,
      gender: this.profileData?.gender ?? '',
    };

    this.auth
      .UpdateProfile(this.updatePayload)
      .pipe(
        switchMap(() => {
          this.toastr.info('Initiating payment..');
          return this.auth.bookAppointment(appointmentPayload);
        }),
        switchMap((res: any) => {
          console.log('appointment created', res);
          localStorage.setItem('appointment_id', res.appointment_id);

          if (res.error) {
            this.toastr.error(res.msg || 'Slot unavailable');
            return EMPTY;
          }

          // Use the GST/platform-fee-inclusive amount from the preview API
          // as the source of truth for what Razorpay actually charges.
          // Guaranteed valid here — payAppointment() blocks earlier when the
          // preview failed or final_amount is missing/zero.
          const chargeAmount = this.finalAmount;

          const payload = {
            user_id: localStorage.getItem('PK') || localStorage.getItem('user_id'),
            appointment_id: res.appointment_id,
            lawyer_id: this.lawyer.lawyer_id,
            amount: chargeAmount,
            slot_id: this.slot.slot_id,
          };

          return this.auth.createOrder(payload);
        }),
        catchError((err) => {
          this.toastr.error(
            // err?.error?.data?.msg ||
            //   err?.error?.msg ||
            //   err?.message ||
              'Something went wrong',
          );
          return EMPTY;
        }),
      )
      .subscribe((res: any) => {
        if (!res) return;

        const order = res.data;

        const options = {
          key: order.checkout_options.key,
          order_id: order.order_id,
          ...order.checkout_options,

          handler: (response: any) => {
            console.log('Payment success:', response);
            this.ngZone.run(() => {
              this.auth
                .verifyPayment({
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signatureF,
                })
                .subscribe((res: any) => {
                  if (res.status) {
                    console.log('Payment successful');
                  }
                });
              this.toastr.success('Appointment booked successfully...');
              this.router.navigate(['/consumer/appointments']);
            });
          },

          modal: {
            ondismiss: () => {
              this.ngZone.run(() => {
                this.toastr.error('Payment cancelled');
              });
            },
          },
        };

        const rzp = new (window as any).Razorpay(options);
        rzp.open();

        rzp.on('payment.failed', (response: any) => {
          this.ngZone.run(() => {
            this.toastr.error(response.error.description || 'Payment failed');
          });
        });
      });
  }

  getCheckoutPreview() {
    if (!this.lawyer?.amount) {
      // No base amount to price against — block booking rather than
      // charging an undefined/zero amount.
      this.previewError = true;
      return;
    }

    this.loadingPriceSummary = true;
    this.previewError = false;
    this.auth.getCheckoutPreview({ amount: Number(this.lawyer.amount) }).subscribe({
      next: (res: any) => {
        this.loadingPriceSummary = false;

        // New preview format: { context, currency, line_items[], totals }
        // with the payable amount at totals.final_amount.
        if (res?.totals?.final_amount > 0) {
          this.priceSummary = {
            ...res,
            line_items: res.line_items ?? [],
          };
        } else {
          // API responded but without a usable price summary — treat the
          // same as a failure so the user can't book without a final amount.
          this.previewError = true;
        }
      },
      error: () => {
        this.loadingPriceSummary = false;
        this.previewError = true;
      },
    });
  }

  applyCoupon() {
    if (!this.couponCode?.trim()) return;

    this.applyingCoupon = true;
    this.couponError = '';
    this.couponMessage = '';

    // this.auth.validateCoupon?.({ code: this.couponCode.trim() }).subscribe({
    //   next: (res: any) => {
    //     this.applyingCoupon = false;
    //     if (res?.valid) {
    //       this.couponApplied = true;
    //       this.couponDiscount = res.discount_amount || 0;
    //       this.couponMessage = `Coupon applied — ₹${this.couponDiscount} off`;
    //     } else {
    //       this.couponError = res?.message || 'Invalid coupon code';
    //     }
    //   },
    //   error: () => {
    //     this.applyingCoupon = false;
    //     this.couponError = 'Could not validate coupon. Try again.';
    //   }
    // });
  }

  removeCoupon() {
    this.couponApplied = false;
    this.couponDiscount = 0;
    this.couponCode = '';
    this.couponMessage = '';
    this.couponError = '';
  }
}
